import React from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { Page } from 'components/core/Page';

import { HomePageHero } from 'components/landing/HomePageHero';
import { HowItWorksSection } from 'components/brand/HowItWorksSection';
import { StorageTypesSection } from 'components/brand/StorageTypesSection';
import { SizeGuideSection } from 'components/brand/SizeGuideSection';
import { ReviewSection } from 'components/brand/ReviewSection';
import { FeaturedCitiesSection } from 'components/brand/FeaturedCitiesSection';
import { PLPFacilitiesSection } from 'components/plp/PLPFacilitiesSection';
import { FeaturedContent } from 'components/organisms';
import useExperiment from 'hooks/useExperiment';

import './homepage.scss';

export const enhance = (Component) =>
	withPageViewTracking(PAGE_TYPES.HOME_PAGE)(Component);

function buildBlogItemListSchema(posts) {
	const validPosts = posts.filter(post => post.link && post.content);
	return JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		'name': 'Featured Storage Blogs',
		'description': 'A curated list of featured blog articles on the homepage',
		'itemListOrder': 'https://schema.org/ItemListOrderAscending',
		'numberOfItems': validPosts.length,
		'itemListElement': validPosts.map((post, i) => ({
			'@type': 'ListItem',
			'position': i + 1,
			'url': post.link,
			'name': post.content,
		})),
	});
}

export function HomePage() {
	const featuredCities = useSelector((state) => state.content.featuredCities);
	const featuredContent = useSelector((state) => state.content.featuredContent);
	const baseUrl = useSelector((state) => state.app.baseUrl);
	const { variation: sf15342Variation } = useExperiment('sf_15342_geolocation_sfdc');
	return (
		<Page
			className="homepage"
			pageType={PAGE_TYPES.HOME}
			landingPage
		>
			<Helmet>
				<meta
					name="msvalidate.01"
					content="72430AA401040F456AF99A586F12B284"
				/>
				<link
					rel="canonical"
					href={baseUrl}
				/>
				<script type="application/ld+json">
					{`
						{
							"@context": "http://schema.org",
							"@type": "WebSite",
							"url": "https://www.sparefoot.com",
							"potentialAction": {
								"@type": "SearchAction",
								"target": "https://www.sparefoot.com/search?location={search_term_string}",
								"query-input": "required name=search_term_string"
							}
						}
					`}
				</script>
				<script type="application/ld+json">
					{`
						{
							"name": "Sparefoot",
							"@context": "http://schema.org",
							"@type": ["Organization", "OnlineBusiness"],
							"url": "https://www.sparefoot.com",
							"logo": "https://www.sparefoot.com/logo.svg",
							"sameAs": [
								"https://twitter.com/SpareFoot",
								"https://www.facebook.com/SpareFoot",
								"https://www.instagram.com/sparefoot/",
								"https://www.pinterest.com/sparefoot/"
							],
							"contactPoint": [
								{
								"@type": "ContactPoint",
								"telephone": "+1-512-705-6208",
								"contactType": "customer service"
								}
							],
							"founder": "Chuck Gordon",
							"foundingDate": 2008
						}
					`}
				</script>
				{featuredContent && !isEmpty(featuredContent.contentCards) && (
					<script type="application/ld+json">
						{buildBlogItemListSchema(featuredContent.contentCards)}
					</script>
				)}
			</Helmet >
			<HomePageHero />
			{sf15342Variation === 'variation1' && ( // sf_15342_geolocation_sfdc
				<PLPFacilitiesSection />
			)}
			<HowItWorksSection />
			<SizeGuideSection />
			<StorageTypesSection />
			<ReviewSection />
			{featuredContent && !isEmpty(featuredContent.contentCards) && (
				<FeaturedContent
					title="Storage Tips Worth Reading"
					description={featuredContent.description}
					contentCards={featuredContent.contentCards}
					pageType={featuredContent.pageType}
					ctaButton={{ label: 'Get More Moving & Storage Tips', href: 'https://www.sparefoot.com/blog/' }}
					moduleType="blogPosts"
				/>
			)}
			{!isEmpty(featuredCities) && (
				<FeaturedCitiesSection
					cities={featuredCities}
					fillOrder="vertical"
				/>
			)}
		</Page>
	);
}

HomePage.propTypes = {
	featuredCities: PropTypes.arrayOf(PropTypes.object),
	featuredContent: PropTypes.shape({
		title: PropTypes.string,
		description: PropTypes.string,
		pageType: PropTypes.string,
		contentCards: PropTypes.array,
	}),
	baseUrl: PropTypes.string
};

export default enhance(HomePage);
